import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Grid,
  Button,
  Stack,
  Paper,
  IconButton,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { Link } from 'react-router-dom';

// Icons
import LocationOnIcon       from '@mui/icons-material/LocationOn';
import ArrowForwardIcon     from '@mui/icons-material/ArrowForward';
import ArrowBackIosNewIcon  from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon  from '@mui/icons-material/ArrowForwardIos';
import EmojiEventsIcon      from '@mui/icons-material/EmojiEvents';
import PeopleIcon           from '@mui/icons-material/People';
import SchoolIcon           from '@mui/icons-material/School';
import LocalParkingIcon     from '@mui/icons-material/LocalParking';
import KitchenIcon          from '@mui/icons-material/Kitchen';
import RestaurantMenuIcon   from '@mui/icons-material/RestaurantMenu';
import GroupsIcon           from '@mui/icons-material/Groups';
import VerifiedIcon         from '@mui/icons-material/Verified';
import FavoriteIcon         from '@mui/icons-material/Favorite';
import LightbulbIcon        from '@mui/icons-material/Lightbulb';
import SecurityIcon         from '@mui/icons-material/Security';
import HandshakeIcon        from '@mui/icons-material/Handshake';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FormatQuoteIcon      from '@mui/icons-material/FormatQuote';
import CheckCircleIcon      from '@mui/icons-material/CheckCircle';
import TouchAppIcon         from '@mui/icons-material/TouchApp';
import MenuBookIcon         from '@mui/icons-material/MenuBook';
import CardMembershipIcon   from '@mui/icons-material/CardMembership';
import BuildIcon            from '@mui/icons-material/Build';
import SupportAgentIcon     from '@mui/icons-material/SupportAgent';

// Images
import aboutHero from '../assets/image1.png';
import equipe    from '../assets/logosquare.jpg';
import batisse1  from '../assets/batisse/batisse1.jpeg';
import batisse2  from '../assets/batisse/batisse2.jpeg';
import batisse3  from '../assets/batisse/batisse3.jpeg';
import batisse4  from '../assets/batisse/batisse4.jpeg';
import batisse5  from '../assets/batisse/batisse5.jpeg';
import local1    from '../assets/local/local1.jpeg';
import local2    from '../assets/local/local2.jpeg';
import local3    from '../assets/local/local3.jpeg';
import local4    from '../assets/local/local4.jpeg';

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const bounceY = keyframes`
  0%,100% { transform: translateY(0); }
  50%      { transform: translateY(8px); }
`;


const VALEURS = [
  { icon: <EmojiEventsIcon />,      label: 'Excellence',          desc: 'Formations de haute qualité certifiées'         },
  { icon: <HandshakeIcon   />,      label: 'Engagement',          desc: 'Dévoués à la réussite de chaque apprenant'      },
  { icon: <SecurityIcon    />,      label: 'Sécurité',            desc: 'La prévention comme priorité absolue'           },
  { icon: <PeopleIcon      />,      label: 'Partage',             desc: 'Transmission des savoirs qui sauvent des vies'  },
  { icon: <LightbulbIcon   />,      label: 'Innovation',          desc: 'Méthodes pédagogiques modernes et pratiques'    },
  { icon: <VerifiedIcon    />,      label: 'Responsabilité',      desc: 'Formateurs certifiés et engagés'                },
  { icon: <FavoriteIcon    />,      label: 'Empathie',            desc: 'À l\'écoute de chaque participant'              },
  { icon: <WorkspacePremiumIcon />, label: 'Professionnalisme',   desc: 'Standards Croix-Rouge canadienne'               },
];

const SERVICES = [
  { icon: <LocalParkingIcon   sx={{ fontSize: 34 }} />, title: 'Stationnement',          text: 'Gratuit et disponible sur place'                      },
  { icon: <KitchenIcon        sx={{ fontSize: 34 }} />, title: 'Installations',          text: 'Micro-ondes, réfrigérateur et cuisine disponibles'    },
  { icon: <RestaurantMenuIcon sx={{ fontSize: 34 }} />, title: 'Restauration',           text: 'Plusieurs options à proximité à pied'                 },
  { icon: <GroupsIcon         sx={{ fontSize: 34 }} />, title: 'Formations sur mesure',  text: 'Déplacement dans vos locaux pour groupes de 8+'       },
];

const POURQUOI = [
  {
    icon: <WorkspacePremiumIcon sx={{ fontSize: 34 }} />,
    title: 'Partenaire officiel Croix-Rouge',
    text:  'Nos certifications sont reconnues à l\'échelle nationale. Chaque formation suit les standards rigoureux de la Croix-Rouge canadienne.',
  },
  {
    icon: <BuildIcon     sx={{ fontSize: 34 }} />,
    title: 'Formation axée sur la pratique',
    text:  'Chaque cours privilégie les mises en situation réelles. Vous repartez avec des gestes maîtrisés, pas seulement théoriques.',
  },
  {
    icon: <SupportAgentIcon sx={{ fontSize: 34 }} />,
    title: 'Formateurs passionnés & certifiés',
    text:  'Nos moniteurs combinent expérience terrain et pédagogie bienveillante pour un apprentissage humain et efficace.',
  },
  {
    icon: <GroupsIcon    sx={{ fontSize: 34 }} />,
    title: 'Groupes & particuliers',
    text:  'Cours publics, privés ou sur mesure. Nous nous déplaçons dans vos locaux pour former vos équipes dès 8 participants.',
  },
];

const ETAPES = [
  { num: '01', icon: <MenuBookIcon   sx={{ fontSize: 28 }} />, title: 'Choisissez votre formation', text: 'Parcourez notre catalogue et sélectionnez la formation adaptée à votre besoin.' },
  { num: '02', icon: <TouchAppIcon   sx={{ fontSize: 28 }} />, title: 'Inscrivez-vous en ligne',    text: 'Inscription simple et rapide sur notre site. Confirmation immédiate par courriel.' },
  { num: '03', icon: <SchoolIcon     sx={{ fontSize: 28 }} />, title: 'Suivez votre formation',     text: 'Apprenez les gestes qui sauvent avec nos formateurs certifiés dans nos locaux modernes.' },
  { num: '04', icon: <CardMembershipIcon sx={{ fontSize: 28 }} />, title: 'Obtenez votre certificat', text: 'Recevez votre certification Croix-Rouge reconnue partout au Canada.' },
];

// ─── Styled ───────────────────────────────────────────────────────────────────
const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  background: `linear-gradient(150deg, rgba(183,28,28,0.97) 0%, rgba(100,0,0,0.92) 100%), url(${aboutHero})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  color: '#fff',
  padding: theme.spacing(18, 0, 16),
  textAlign: 'center',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -1,
    left: 0,
    right: 0,
    height: '70px',
    background: '#ffffff',
    clipPath: 'ellipse(60% 100% at 50% 100%)',
  },
  // Décoration cercles
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-80px',
    right: '-80px',
    width: '360px',
    height: '360px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.04)',
    pointerEvents: 'none',
  },
}));

const HeroCircle = styled(Box)(() => ({
  position: 'absolute',
  bottom: '80px',
  left: '-60px',
  width: '260px',
  height: '260px',
  borderRadius: '50%',
  background: 'rgba(255,255,255,0.04)',
  pointerEvents: 'none',
}));

// Section avec fond alterné
const Section = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'alt',
})(({ theme, alt }) => ({
  padding: theme.spacing(9, 0),
  backgroundColor: alt ? '#f7f8fc' : '#ffffff',
  width: '100%',
  position: 'relative',
}));

// Wrapper centré
const Wrap = styled(Box)(({ theme }) => ({
  maxWidth: '1160px',
  margin: '0 auto',
  padding: theme.spacing(0, 3),
}));

const SectionLabel = styled(Typography)(() => ({
  fontSize: '0.78rem',
  fontWeight: 700,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#c62828',
  marginBottom: '10px',
  display: 'block',
  textAlign: 'center',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: 'relative',
  paddingBottom: theme.spacing(2.5),
  marginBottom: theme.spacing(5),
  textAlign: 'center',
  fontWeight: 800,
  color: '#1a1a1a',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '44px',
    height: '4px',
    background: 'linear-gradient(90deg, #c62828, #ff6b6b)',
    borderRadius: '2px',
  },
}));


// Carte valeur
const ValueCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3, 2),
  borderRadius: theme.spacing(2),
  textAlign: 'center',
  border: '1px solid rgba(198,40,40,0.1)',
  boxShadow: '0 3px 16px rgba(0,0,0,0.04)',
  transition: 'all 0.28s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1.2),
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 32px rgba(198,40,40,0.13)',
    borderColor: '#c62828',
  },
}));

// Carte service
const ServiceCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4, 3),
  borderRadius: theme.spacing(2.5),
  textAlign: 'center',
  border: '1px solid rgba(0,0,0,0.06)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  transition: 'all 0.28s ease',
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #c62828, #ff6b6b)',
    transform: 'scaleX(0)',
    transformOrigin: 'left',
    transition: 'transform 0.3s ease',
  },
  '&:hover::before': { transform: 'scaleX(1)' },
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 14px 36px rgba(198,40,40,0.1)',
  },
}));

// Carte "Pourquoi"
const WhyCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2.5),
  border: '1px solid rgba(0,0,0,0.07)',
  boxShadow: '0 3px 16px rgba(0,0,0,0.05)',
  transition: 'all 0.3s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  '&:hover': {
    boxShadow: '0 14px 40px rgba(198,40,40,0.12)',
    transform: 'translateY(-4px)',
  },
}));

// Cercle numéro étape
const StepCircle = styled(Box)(() => ({
  width: 56,
  height: 56,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #c62828, #8e0000)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  fontWeight: 800,
  fontSize: '1.1rem',
  flexShrink: 0,
  boxShadow: '0 6px 20px rgba(198,40,40,0.3)',
}));

// Carrousel
const CarouselContainer = styled(Box)(() => ({
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  borderRadius: '20px',
  boxShadow: '0 16px 48px rgba(0,0,0,0.14)',
}));

const CarouselTrack = styled(Box)(() => ({
  display: 'flex',
  transition: 'transform 0.5s ease-in-out',
}));

const CarouselImage = styled('img')(() => ({
  width: '100%',
  objectFit: 'cover',
  flexShrink: 0,
}));

const CarouselBtn = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(255,255,255,0.93)',
  color: '#c62828',
  width: 40,
  height: 40,
  boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
  zIndex: 10,
  '&:hover': { backgroundColor: '#c62828', color: '#fff' },
  '&.left':  { left:  theme.spacing(1.5) },
  '&.right': { right: theme.spacing(1.5) },
}));

const DotsRow = ({ images, index, setIndex }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, py: 1.5 }}>
    {images.map((_, i) => (
      <Box
        key={i}
        onClick={() => setIndex(i)}
        sx={{
          width: i === index ? 22 : 8,
          height: 8,
          borderRadius: '4px',
          backgroundColor: i === index ? '#c62828' : 'rgba(198,40,40,0.25)',
          cursor: 'pointer',
          transition: 'all 0.3s',
        }}
      />
    ))}
  </Box>
);

// ─── Icône arrondie réutilisable ──────────────────────────────────────────────
const RoundIcon = ({ children, size = 56 }) => (
  <Box sx={{
    width: size, height: size,
    borderRadius: `${size * 0.3}px`,
    background: 'linear-gradient(135deg, rgba(198,40,40,0.1), rgba(198,40,40,0.18))',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#c62828', flexShrink: 0,
  }}>
    {children}
  </Box>
);

// ─── Composant principal ──────────────────────────────────────────────────────
const About = () => {
  const batisseImages = [batisse1, batisse2, batisse3, batisse4, batisse5];
  const localImages   = [local1, local2, local3, local4];
  // Salle polyvalente (Templateimg) retirée — uniquement batisse2 et local1
  const salleImages   = [batisse2, local1];
  const salleLabels   = ['Salle de formation principale', 'Espace travaux pratiques'];

  const [batisseIdx, setBatisseIdx] = useState(0);
  const [localIdx,   setLocalIdx  ] = useState(0);
  const [salleIdx,   setSalleIdx  ] = useState(0);


  // Auto-scroll carrousels
  useEffect(() => { const t = setInterval(() => setBatisseIdx(p => (p+1) % batisseImages.length), 5000); return () => clearInterval(t); }, [batisseImages.length]);
  useEffect(() => { const t = setInterval(() => setLocalIdx(p   => (p+1) % localImages.length  ), 5500); return () => clearInterval(t); }, [localImages.length]);
  useEffect(() => { const t = setInterval(() => setSalleIdx(p   => (p+1) % salleImages.length  ), 4500); return () => clearInterval(t); }, [salleImages.length]);

  return (
    <>
      {/* ═══════════════════════════ HERO ═══════════════════════════ */}
      <HeroSection>
        <HeroCircle />
        <Wrap sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ animation: `${fadeUp} 0.8s ease both` }}>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1,
              px: 2.5, py: 0.75,
              borderRadius: '50px',
              border: '1px solid rgba(255,255,255,0.35)',
              background: 'rgba(255,255,255,0.12)',
              backdropFilter: 'blur(4px)',
              mb: 3,
            }}>
              <VerifiedIcon sx={{ fontSize: '1rem' }} />
              <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: '0.1em' }}>
                Partenaire officiel · Croix-Rouge canadienne
              </Typography>
            </Box>

            <Typography variant="h1" sx={{ fontSize: { xs: '2.4rem', md: '3.8rem' }, fontWeight: 900, mb: 2, lineHeight: 1.1 }}>
              À Propos de Nous
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.4rem', md: '2rem' }, fontWeight: 600, mb: 4, opacity: 0.9 }}>
              Académie Vita Secours — Saint-Jérôme
            </Typography>

            <Typography sx={{
              maxWidth: '640px', mx: 'auto',
              fontSize: '1.1rem', lineHeight: 1.9, opacity: 0.88,
            }}>
              Formation secourisme et RCR certifiée, axée sur la pratique et adaptée à tous les profils.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" mt={5}>
              <Button component={Link} to="/formations" variant="contained"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  background: '#fff', color: '#c62828',
                  fontWeight: 700, px: 4, py: 1.4, borderRadius: '50px',
                  '&:hover': { background: 'rgba(255,255,255,0.9)', transform: 'translateY(-2px)' },
                }}>
                Nos formations
              </Button>
              <Button component={Link} to="/contact" variant="outlined"
                sx={{
                  borderColor: 'rgba(255,255,255,0.6)', color: '#fff',
                  fontWeight: 700, px: 4, py: 1.4, borderRadius: '50px',
                  '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.1)' },
                }}>
                Nous contacter
              </Button>
            </Stack>
          </Box>
          <Box sx={{ mt: 6, animation: `${bounceY} 2.2s ease infinite` }}>
            <KeyboardArrowDownIcon sx={{ fontSize: 32, opacity: 0.55 }} />
          </Box>
        </Wrap>
      </HeroSection>


      {/* ════════════════ QUI SOMMES-NOUS ════════════════════════════ */}
      <Section alt>
        <Wrap>
          <SectionLabel>Notre histoire</SectionLabel>
          <SectionTitle variant="h3">Qui sommes-nous ?</SectionTitle>
          <Grid container spacing={6} alignItems="center" justifyContent="center">

            {/* Image */}
            <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ position: 'relative', width: '100%', maxWidth: 420 }}>
                <Box
                  component="img"
                  src={equipe}
                  alt="Académie Vita Secours"
                  sx={{
                    width: '100%',
                    borderRadius: '24px',
                    boxShadow: '0 24px 60px rgba(0,0,0,0.14)',
                    objectFit: 'contain',
                    display: 'block',
                    backgroundColor: '#fff',
                    p: 2,
                  }}
                />
                {/* Badge décoratif */}
                <Box sx={{
                  position: 'absolute', bottom: -14, right: -14,
                  background: 'linear-gradient(135deg, #c62828, #8e0000)',
                  color: '#fff', borderRadius: '16px',
                  p: '10px 18px',
                  display: 'flex', alignItems: 'center', gap: 1,
                  boxShadow: '0 8px 24px rgba(198,40,40,0.35)',
                }}>
                  <VerifiedIcon sx={{ fontSize: '1rem' }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                    Certifié Croix-Rouge
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Texte */}
            <Grid item xs={12} md={7}>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.9, color: '#4a4a4a', mb: 2.5 }}>
                  <strong>Bienvenue à l'Académie Vita Secours</strong>, votre école de formation en secourisme et RCR à{' '}
                  <strong style={{ color: '#c62828' }}>Saint-Jérôme</strong>.
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.9, color: '#4a4a4a', mb: 2.5 }}>
                  En tant que <strong>partenaire officiel de la Croix-Rouge canadienne</strong>, nous offrons des cours reconnus,
                  dynamiques et axés sur la pratique, afin de préparer chacun à réagir efficacement lors d'une urgence.
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.9, color: '#4a4a4a', mb: 4 }}>
                  Que vous soyez citoyen, parent, employé ou professionnel de la santé — nous avons la formation adaptée.
                </Typography>

                {/* Points clés */}
                <Stack spacing={1.5}>
                  {[
                    'Formations reconnues à l\'échelle nationale',
                    'Cours publics, privés et sur mesure',
                    'Déplacement possible dans vos locaux (8+ pers.)',
                  ].map((point, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                      <CheckCircleIcon sx={{ color: '#c62828', fontSize: '1.1rem', flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ color: '#555', fontWeight: 500 }}>{point}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Wrap>
      </Section>

      {/* ═══════════════════ NOTRE MISSION ══════════════════════════ */}
      <Section>
        <Wrap>
          <Box sx={{
            background: 'linear-gradient(135deg, #b71c1c 0%, #7f0000 100%)',
            borderRadius: '28px',
            p: { xs: 5, md: 8 },
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 24px 64px rgba(198,40,40,0.28)',
            // Cercles déco
            '&::before': {
              content: '""', position: 'absolute',
              top: -70, right: -70,
              width: 280, height: 280,
              borderRadius: '50%', background: 'rgba(255,255,255,0.05)',
            },
            '&::after': {
              content: '""', position: 'absolute',
              bottom: -50, left: -50,
              width: 200, height: 200,
              borderRadius: '50%', background: 'rgba(255,255,255,0.04)',
            },
          }}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <FormatQuoteIcon sx={{ fontSize: 52, color: 'rgba(255,255,255,0.22)', mb: 1 }} />
              <Typography variant="h3" sx={{ fontWeight: 800, color: '#fff', mb: 3, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Notre mission
              </Typography>
              <Typography sx={{
                fontSize: { xs: '1.2rem', md: '1.55rem' },
                fontWeight: 700, color: '#fff', lineHeight: 1.65,
                mb: 3.5, maxWidth: '720px', mx: 'auto',
              }}>
                Transmettre les bons gestes et les bonnes techniques de secourisme au bon moment, pour sauver des vies.
              </Typography>
              <Box sx={{ width: 44, height: 3, background: 'rgba(255,255,255,0.35)', borderRadius: 2, mx: 'auto', mb: 3.5 }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.86)', fontSize: '1.05rem', lineHeight: 1.8, maxWidth: '680px', mx: 'auto', mb: 3.5 }}>
                Nos moniteurs certifiés, passionnés par la prévention et l'intervention, mettent tout en œuvre pour offrir une
                expérience d'apprentissage humaine, motivante et concrète.
              </Typography>
              <Typography sx={{ color: '#ffcdd2', fontWeight: 700, fontStyle: 'italic', fontSize: '1rem' }}>
                « Nous croyons fermement que chaque personne peut faire la différence ! »
              </Typography>
            </Box>
          </Box>
        </Wrap>
      </Section>

      {/* ═══════════════ POURQUOI NOUS CHOISIR ══════════════════════ */}
      <Section alt>
        <Wrap>
          <SectionLabel>Nos différences</SectionLabel>
          <SectionTitle variant="h3">Pourquoi choisir Vita Secours ?</SectionTitle>
          <Grid container spacing={3} justifyContent="center">
            {POURQUOI.map((item, i) => (
              <Grid key={i} item xs={12} sm={6}>
                <WhyCard elevation={1}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <RoundIcon size={52}>{item.icon}</RoundIcon>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.75, fontSize: '1rem' }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.7 }}>
                        {item.text}
                      </Typography>
                    </Box>
                  </Box>
                </WhyCard>
              </Grid>
            ))}
          </Grid>
        </Wrap>
      </Section>

      {/* ══════════════ NOTRE APPROCHE EN ÉTAPES ════════════════════ */}
      <Section>
        <Wrap>
          <SectionLabel>Simple et efficace</SectionLabel>
          <SectionTitle variant="h3">Notre approche en 4 étapes</SectionTitle>

          {/* Ligne de progression visible seulement sur desktop */}
          <Box sx={{ position: 'relative' }}>
            <Box sx={{
              display: { xs: 'none', md: 'block' },
              position: 'absolute',
              top: 28,           // centre du StepCircle (56px / 2 = 28px)
              left: '12.5%',     // commence au centre du 1er cercle (~1/8 de la largeur)
              right: '12.5%',    // finit au centre du 4e cercle
              height: '2px',
              background: 'linear-gradient(90deg, #c62828 0%, rgba(198,40,40,0.25) 100%)',
              borderRadius: '1px',
              zIndex: 0,
            }} />

            <Grid container spacing={3} justifyContent="center" sx={{ position: 'relative', zIndex: 1 }}>
              {ETAPES.map((e, i) => (
                <Grid key={i} item xs={12} sm={6} md={3}>
                  <Box sx={{
                    textAlign: 'center',
                    p: 3,
                    pt: 0,
                    borderRadius: '20px',
                    height: '100%',
                    transition: 'all 0.28s',
                  }}>
                    {/* Cercle numéro centré au-dessus de la ligne */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
                      <StepCircle>{e.num}</StepCircle>
                    </Box>

                    {/* Carte de contenu */}
                    <Box sx={{
                      p: 2.5,
                      borderRadius: '16px',
                      border: '1px solid rgba(0,0,0,0.07)',
                      background: '#fff',
                      boxShadow: '0 3px 14px rgba(0,0,0,0.05)',
                      transition: 'all 0.28s',
                      '&:hover': { boxShadow: '0 12px 36px rgba(198,40,40,0.1)', transform: 'translateY(-4px)' },
                    }}>
                      <Box sx={{ mb: 1.5, color: '#c62828', display: 'flex', justifyContent: 'center' }}>
                        {e.icon}
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.75, fontSize: '0.95rem' }}>
                        {e.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#777', lineHeight: 1.65, fontSize: '0.84rem' }}>
                        {e.text}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Wrap>
      </Section>

      {/* ══════════════════ NOS VALEURS ════════════════════════════ */}
      <Section alt>
        <Wrap>
          <SectionLabel>Ce qui nous définit</SectionLabel>
          <SectionTitle variant="h3">Nos valeurs</SectionTitle>
          <Grid container spacing={2.5} justifyContent="center">
            {VALEURS.map((v, i) => (
              <Grid key={i} item xs={6} sm={4} md={3}>
                <ValueCard elevation={0}>
                  <RoundIcon size={50}>{v.icon}</RoundIcon>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '0.92rem' }}>
                    {v.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#777', lineHeight: 1.55, fontSize: '0.81rem' }}>
                    {v.desc}
                  </Typography>
                </ValueCard>
              </Grid>
            ))}
          </Grid>
        </Wrap>
      </Section>

      {/* ══════════════════ NOS INSTALLATIONS ════════════════════════ */}
      <Section>
        <Wrap>
          <SectionLabel>Nos locaux</SectionLabel>
          <SectionTitle variant="h3">Nos installations</SectionTitle>

          {/* Bâtisse */}
          <Box sx={{ mb: 7 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3 }}>
              <LocationOnIcon sx={{ color: '#c62828' }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a' }}>Notre bâtisse</Typography>
            </Box>
            <CarouselContainer>
              <CarouselTrack sx={{ transform: `translateX(-${batisseIdx * 100}%)` }}>
                {batisseImages.map((img, i) => (
                  <CarouselImage key={i} src={img} alt={`Bâtisse ${i+1}`} sx={{ height: '420px' }} />
                ))}
              </CarouselTrack>
              <CarouselBtn className="left"  onClick={() => setBatisseIdx(p => (p-1+batisseImages.length)%batisseImages.length)}><ArrowBackIosNewIcon sx={{ fontSize: '0.85rem' }} /></CarouselBtn>
              <CarouselBtn className="right" onClick={() => setBatisseIdx(p => (p+1)%batisseImages.length)}><ArrowForwardIosIcon sx={{ fontSize: '0.85rem' }} /></CarouselBtn>
              <DotsRow images={batisseImages} index={batisseIdx} setIndex={setBatisseIdx} />
            </CarouselContainer>
          </Box>

          {/* Locaux de formation */}
          <Box sx={{ mb: 7 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3 }}>
              <LocationOnIcon sx={{ color: '#c62828' }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a' }}>Nos locaux de formation</Typography>
            </Box>
            <Grid container spacing={5} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.9, color: '#4a4a4a', mb: 2 }}>
                    La majorité des cours ont lieu à notre local principal au{' '}
                    <strong style={{ color: '#c62828' }}>275 Des Laurentides, Saint-Jérôme</strong>.
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.9, color: '#4a4a4a' }}>
                    Nos moniteurs peuvent aussi se déplacer dans vos locaux si vous formez un groupe de{' '}
                    <strong>8 personnes et plus</strong>.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <CarouselContainer>
                  <CarouselTrack sx={{ transform: `translateX(-${localIdx * 100}%)` }}>
                    {localImages.map((img, i) => (
                      <CarouselImage key={i} src={img} alt={`Local ${i+1}`} sx={{ height: '300px' }} />
                    ))}
                  </CarouselTrack>
                  <CarouselBtn className="left"  onClick={() => setLocalIdx(p => (p-1+localImages.length)%localImages.length)}><ArrowBackIosNewIcon sx={{ fontSize: '0.8rem' }} /></CarouselBtn>
                  <CarouselBtn className="right" onClick={() => setLocalIdx(p => (p+1)%localImages.length)}><ArrowForwardIosIcon sx={{ fontSize: '0.8rem' }} /></CarouselBtn>
                  <DotsRow images={localImages} index={localIdx} setIndex={setLocalIdx} />
                </CarouselContainer>
              </Grid>
            </Grid>
          </Box>

          {/* Salles de formation */}
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a', textAlign: 'center', mb: 3 }}>
              Nos salles de formation
            </Typography>
            <CarouselContainer>
              <CarouselTrack sx={{ transform: `translateX(-${salleIdx * 100}%)` }}>
                {salleImages.map((img, i) => (
                  <Box key={i} sx={{ position: 'relative', width: '100%', flexShrink: 0 }}>
                    <CarouselImage src={img} alt={salleLabels[i]} sx={{ height: '420px' }} />
                    <Box sx={{
                      position: 'absolute', bottom: 0, left: 0, right: 0,
                      background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                      p: 3, textAlign: 'center',
                    }}>
                      <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                        {salleLabels[i]}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </CarouselTrack>
              <CarouselBtn className="left"  onClick={() => setSalleIdx(p => (p-1+salleImages.length)%salleImages.length)}><ArrowBackIosNewIcon sx={{ fontSize: '0.85rem' }} /></CarouselBtn>
              <CarouselBtn className="right" onClick={() => setSalleIdx(p => (p+1)%salleImages.length)}><ArrowForwardIosIcon sx={{ fontSize: '0.85rem' }} /></CarouselBtn>
              <DotsRow images={salleImages} index={salleIdx} setIndex={setSalleIdx} />
            </CarouselContainer>
          </Box>
        </Wrap>
      </Section>

      {/* ══════════════════ SERVICES PRATIQUES ═══════════════════════ */}
      <Section alt>
        <Wrap>
          <SectionLabel>Sur place</SectionLabel>
          <SectionTitle variant="h3">Services pratiques</SectionTitle>
          <Grid container spacing={3} justifyContent="center">
            {SERVICES.map((s, i) => (
              <Grid key={i} item xs={12} sm={6} md={3}>
                <ServiceCard elevation={1}>
                  <RoundIcon size={60}>{s.icon}</RoundIcon>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '1rem', textAlign: 'center' }}>
                    {s.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.65, textAlign: 'center' }}>
                    {s.text}
                  </Typography>
                </ServiceCard>
              </Grid>
            ))}
          </Grid>
        </Wrap>
      </Section>

      {/* ══════════════════ NOTRE ÉQUIPE ═════════════════════════════ */}
      <Section>
        <Wrap>
          <SectionLabel>Les gens derrière Vita Secours</SectionLabel>
          <SectionTitle variant="h3">Notre équipe</SectionTitle>
          <Grid container spacing={6} alignItems="center" justifyContent="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.9, color: '#4a4a4a', mb: 2.5 }}>
                  Notre équipe est composée de{' '}
                  <strong style={{ color: '#c62828' }}>moniteurs certifiés par la Croix-Rouge canadienne</strong>,
                  passionnés par la transmission du savoir et la prévention.
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.9, color: '#4a4a4a', mb: 4 }}>
                  Chaque formateur possède une expérience terrain significative et s'engage à offrir une formation
                  de qualité, adaptée aux besoins spécifiques de chaque apprenant.
                </Typography>
                <Box sx={{
                  p: 3.5,
                  borderRadius: '18px',
                  border: '1px solid rgba(198,40,40,0.15)',
                  borderLeft: '5px solid #c62828',
                  background: 'linear-gradient(135deg, #fff9f9, #fff5f5)',
                  boxShadow: '0 6px 24px rgba(198,40,40,0.06)',
                }}>
                  <FormatQuoteIcon sx={{ color: '#c62828', opacity: 0.35, fontSize: 28, mb: 0.75 }} />
                  <Typography variant="body1" sx={{ fontStyle: 'italic', color: '#333', fontSize: '1.05rem', lineHeight: 1.8 }}>
                    Notre objectif est de vous donner la confiance et les compétences nécessaires pour sauver des vies.
                  </Typography>
                  <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 700, color: '#c62828' }}>
                    — L'équipe Vita Secours
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ position: 'relative', width: '100%', maxWidth: 420 }}>
                <Box
                  component="img"
                  src={equipe}
                  alt="Notre équipe"
                  sx={{
                    width: '100%',
                    borderRadius: '24px',
                    boxShadow: '0 24px 60px rgba(0,0,0,0.13)',
                    objectFit: 'contain',
                    backgroundColor: '#fff',
                    p: 2,
                    display: 'block',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Wrap>
      </Section>

      {/* ══════════════════ PARTENAIRES ══════════════════════════════ */}
      <Section alt>
        <Wrap>
          <SectionLabel>Reconnus et accrédités</SectionLabel>
          <SectionTitle variant="h3">Partenaires & certifications</SectionTitle>
          <Grid container spacing={2.5} justifyContent="center">
            {[
              { icon: <WorkspacePremiumIcon sx={{ color: '#c62828', fontSize: 28 }} />, title: 'Croix-Rouge canadienne', sub: 'Partenaire officiel' },
              { icon: <VerifiedIcon         sx={{ color: '#c62828', fontSize: 28 }} />, title: 'Formation certifiée',   sub: 'Reconnue nationalement' },
              { icon: <SecurityIcon         sx={{ color: '#c62828', fontSize: 28 }} />, title: 'RCR / DEA',            sub: 'Certification professionnelle' },
              { icon: <SchoolIcon           sx={{ color: '#c62828', fontSize: 28 }} />, title: 'Secourisme général',   sub: 'Cours accrédités' },
            ].map((p, i) => (
              <Grid key={i} item xs={12} sm={6} md={3}>
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 2,
                  p: 2.5,
                  borderRadius: '16px',
                  border: '1.5px solid rgba(198,40,40,0.15)',
                  background: '#fff',
                  boxShadow: '0 3px 16px rgba(0,0,0,0.05)',
                  transition: 'all 0.28s',
                  '&:hover': { borderColor: '#c62828', boxShadow: '0 8px 28px rgba(198,40,40,0.13)', transform: 'translateY(-3px)' },
                }}>
                  <RoundIcon size={46}>{p.icon}</RoundIcon>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1.3 }}>
                      {p.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999' }}>{p.sub}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Wrap>
      </Section>

      {/* ══════════════════ CTA ══════════════════════════════════════ */}
      <Section>
        <Wrap>
          <Box sx={{
            textAlign: 'center',
            background: 'linear-gradient(135deg, #fff 0%, #fef7f7 100%)',
            borderRadius: '28px',
            border: '1px solid rgba(198,40,40,0.1)',
            boxShadow: '0 20px 60px rgba(198,40,40,0.08)',
            p: { xs: 5, md: 9 },
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""', position: 'absolute',
              top: 0, left: 0, right: 0, height: '4px',
              background: 'linear-gradient(90deg, #c62828 0%, #ff6b6b 50%, #c62828 100%)',
            },
            '&::after': {
              content: '""', position: 'absolute',
              bottom: -80, right: -80,
              width: 260, height: 260,
              borderRadius: '50%',
              background: 'rgba(198,40,40,0.04)',
              pointerEvents: 'none',
            },
          }}>
            <Typography variant="h3" sx={{ fontWeight: 900, color: '#c62828', mb: 2, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
              Prêt à sauver des vies ?
            </Typography>
            <Typography sx={{ mb: 5, color: '#666', maxWidth: '600px', mx: 'auto', fontSize: '1.1rem', lineHeight: 1.75 }}>
              Rejoignez nos formations certifiées par la Croix-Rouge canadienne et faites la différence autour de vous.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} justifyContent="center">
              <Button component={Link} to="/formations" variant="contained" size="large" endIcon={<ArrowForwardIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #c62828, #8e0000)',
                  color: '#fff', px: 5, py: 1.7, fontWeight: 800, fontSize: '1rem', borderRadius: '50px',
                  boxShadow: '0 10px 30px rgba(198,40,40,0.28)',
                  '&:hover': { background: 'linear-gradient(135deg, #b71c1c, #7f0000)', transform: 'translateY(-3px)', boxShadow: '0 16px 40px rgba(198,40,40,0.38)' },
                }}>
                Découvrir nos formations
              </Button>
              <Button component={Link} to="/contact" variant="outlined" size="large"
                sx={{
                  borderColor: '#c62828', borderWidth: 2, color: '#c62828',
                  px: 5, py: 1.7, fontWeight: 800, fontSize: '1rem', borderRadius: '50px',
                  '&:hover': { borderWidth: 2, borderColor: '#b71c1c', background: 'rgba(198,40,40,0.05)', transform: 'translateY(-3px)' },
                }}>
                Contactez-nous
              </Button>
            </Stack>
          </Box>
        </Wrap>
      </Section>
    </>
  );
};

export default About;
